import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import RightSheet from "../../../components/ui-components/RightSheet";
import Badge from "../../../components/ui-components/Badge";
import { Dropdown } from "../../../components/form-components/Dropdown";
import { previewRule, createRule, setSplit } from "../api";
import { getActiveModelVersion, getCostModelNodes, getCostModelEdges } from "../../cost-allocation/api/costModelApi";
import { buildOwnerTreeOptions } from "../ownerTree";

// Rule types with a one-line explanation each — finance users choosing how to split a
// shared cost need to know what each method does, not just its name.
const RULE_TYPES = [
  { value: "direct", label: "Direct", desc: "All of this cost goes to one owner." },
  { value: "usage_based", label: "Usage-based", desc: "Split across owners by a usage driver." },
  { value: "proportional", label: "Proportional", desc: "Split by owners' existing allocation shares." },
  { value: "fixed", label: "Percentage split", desc: "Divide the cost across owners by fixed percentages." },
];

const DRIVERS = [
  { value: "gcp_usage", label: "GCP Usage" },
  { value: "ai_usage", label: "AI Usage" },
  { value: "bq_jobs", label: "BigQuery Jobs" },
];

const today = () => new Date().toISOString().slice(0, 10);
const euro = (n) => `€${Math.round(n || 0).toLocaleString("en-US")}`;
const round1 = (n) => Math.round((n + Number.EPSILON) * 10) / 10;

// The cost-model catch-all node. Assigning to it is identical to leaving cost unallocated
// (the remainder flows here automatically), so it must never appear as a pickable owner.
const CATCH_ALL_CODE = "__unallocated__";

// Largest-remainder rounding: distribute round(total) euros across weights (which sum to
// ~100) so the integer parts add back up to the total. Finance users won't tolerate
// €5 + €16 ≠ €22 — independent per-line rounding is a trust killer.
const allocateEuros = (total, weights) => {
  const target = Math.round(total || 0);
  const raw = weights.map((w) => ((total || 0) * (w || 0)) / 100);
  const floors = raw.map((v) => Math.floor(v));
  let left = target - floors.reduce((a, b) => a + b, 0);
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  const out = [...floors];
  for (let k = 0; k < left && k < order.length; k++) out[order[k].i] += 1;
  return out;
};

// Maps the engine's strength label to the shared Badge status palette.
const strengthStatus = (label) => {
  switch ((label || "").toLowerCase()) {
    case "high":
      return "success";
    case "medium":
      return "awaiting";
    case "low":
      return "failed";
    default:
      return undefined;
  }
};

const RuleWizardDrawer = ({ open, source, dimension, onClose, onSaved }) => {
  // Edit mode = this source already has an assignment on this dimension (a matrix cell).
  const existingCell = source?.cells?.[dimension?.id];
  const isEditing = !!existingCell;

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]); // cost-model tree edges, for the indented owner picker
  const [nodesLoading, setNodesLoading] = useState(false);
  const [nodesError, setNodesError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [ownerId, setOwnerId] = useState(null); // single-owner path (direct/usage/proportional)
  const [ruleType, setRuleType] = useState("direct");
  const [driver, setDriver] = useState("");
  // Percentage-split path: [{ ownerNodeId, pct }] where pct is a string (whole percent).
  const [splitRows, setSplitRows] = useState([{ ownerNodeId: null, pct: "" }]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(today());
  const [error, setError] = useState(null);

  const isSplit = ruleType === "fixed";

  // Seed the form when the drawer opens. In edit mode, pre-fill rule type / driver / split
  // rows from the existing matrix cell so re-saving never silently converts the rule.
  useEffect(() => {
    if (!open) return;
    const cell = source?.cells?.[dimension?.id];
    const type = cell?.ruleType || "direct";
    setRuleType(type);
    setDriver(cell?.driverKey || "");
    // Pre-fill split rows from the cell's owners (pct is 0..100 from the matrix).
    if (type === "fixed" && Array.isArray(cell?.owners) && cell.owners.length > 0) {
      setSplitRows(cell.owners.map((o) => ({ ownerNodeId: o.ownerNodeId, pct: o.pct != null ? String(round1(o.pct)) : "" })));
    } else {
      setSplitRows([{ ownerNodeId: null, pct: "" }]);
    }
    setEffectiveDate(today());
    setPreview(null);
    setError(null);
  // Resets the form when the drawer opens on a different cell. Keying on source.cells
  // would also reset it on every background refetch, discarding what the user typed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source?.id, dimension?.id]);

  // Fetch cost-model nodes for this dimension. Always resolves to a terminal state
  // (ready / empty / error) so the owner picker can never get silently stuck on "loading".
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setNodes([]);
    setEdges([]);
    setNodesError(null);
    setNodesLoading(true);

    if (!dimension?.id) return () => { cancelled = true; };

    const load = async () => {
      try {
        const version = await getActiveModelVersion(dimension.id);
        const versionId =
          version?.id ?? version?.ID ??
          version?.cost_model_version?.id ?? version?.cost_model_version?.ID;
        if (!versionId) {
          if (!cancelled) { setNodesError("No active cost model for this hierarchy."); setNodesLoading(false); }
          return;
        }
        // Edges drive the indented tree in the owner picker; a failure there must not block
        // owner selection, so fall back to a flat (edgeless) list.
        const [nodeList, edgeList] = await Promise.all([
          getCostModelNodes(versionId).then((l) => l || []),
          getCostModelEdges(versionId).then((l) => l || []).catch(() => []),
        ]);
        if (cancelled) return;
        setNodes(nodeList);
        setEdges(edgeList);
        // Pre-select the single-owner picker to the cell's primary owner (edit) or the first
        // ASSIGNABLE node — never the catch-all, which isn't a real owner.
        const firstAssignable =
          nodeList.find((n) => n.code !== CATCH_ALL_CODE)?.node_id ?? nodeList[0]?.node_id ?? null;
        if (firstAssignable != null) {
          const existingOwnerId = source?.cells?.[dimension?.id]?.ownerNodeId;
          const matched = existingOwnerId != null && nodeList.some((n) => n.node_id === existingOwnerId);
          setOwnerId(matched ? existingOwnerId : firstAssignable);
          // A split row with no owner yet defaults to the first assignable node.
          setSplitRows((rows) =>
            rows.map((r) => (r.ownerNodeId == null ? { ...r, ownerNodeId: firstAssignable } : r))
          );
        }
        setNodesLoading(false);
      } catch (e) {
        if (!cancelled) { setNodesError("Couldn't load owners."); setNodesLoading(false); }
      }
    };
    load();
    return () => { cancelled = true; };
  // Reloads owners when the drawer targets a different cell; source.cells changes on every
  // background refetch and would re-request the list each time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source?.id, dimension?.id, reloadKey]);

  // ── Split helpers ───────────────────────────────────────────────────────
  const splitTotal = splitRows.reduce((sum, r) => sum + (parseFloat(r.pct) || 0), 0);
  const splitOver = splitTotal > 100.0001;
  const splitRemaining = Math.max(0, round1(100 - splitTotal));
  const updateSplitRow = (i, patch) =>
    setSplitRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addSplitRow = () =>
    setSplitRows((rows) => {
      // Default a new row to the first assignable owner not already in the split, so each
      // row starts on a distinct owner (no duplicate to clean up).
      const used = new Set(rows.map((r) => r.ownerNodeId));
      const next = nodes.find((n) => n.code !== CATCH_ALL_CODE && !used.has(n.node_id));
      return [...rows, { ownerNodeId: next?.node_id ?? null, pct: "" }];
    });
  const removeSplitRow = (i) =>
    setSplitRows((rows) => (rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows));

  // Split 100% evenly across the rows (integer percents, largest-remainder so they sum to
  // exactly 100 — e.g. 3 rows → 34/33/33). The most common "carve a shared cost" action.
  const distributeEvenly = () =>
    setSplitRows((rows) => {
      const n = rows.length;
      if (!n) return rows;
      const each = Math.floor(100 / n);
      const left = 100 - each * n;
      return rows.map((r, i) => ({ ...r, pct: String(each + (i < left ? 1 : 0)) }));
    });

  // Add whatever is currently unallocated to one row — the "give the rest to this owner"
  // shortcut finance users reach for constantly.
  const assignRemainderTo = (i) =>
    setSplitRows((rows) =>
      rows.map((r, idx) =>
        idx === i ? { ...r, pct: String(round1((parseFloat(r.pct) || 0) + splitRemaining)) } : r
      )
    );

  // The form is "complete enough" to preview/apply.
  const splitValid =
    splitRows.length > 0 &&
    !splitOver &&
    splitRows.every((r) => r.ownerNodeId != null && (parseFloat(r.pct) || 0) > 0);
  const canPreview = isSplit
    ? splitValid
    : ownerId != null && (ruleType !== "usage_based" || !!driver);

  // Auto-preview just needs the source's total affected cost (owner-independent), so a single
  // representative payload is enough; the per-owner breakdown is computed locally below.
  const buildPreviewPayload = () => ({
    sourceId: source?.id,
    dimensionId: dimension?.id,
    ownerNodeId: isSplit ? splitRows[0]?.ownerNodeId : ownerId,
    ruleType: isSplit ? "fixed" : ruleType,
    ...(ruleType === "usage_based" && { driverKey: driver }),
    ...(isSplit && { fixedPct: (parseFloat(splitRows[0]?.pct) || 0) / 100 }),
  });

  useEffect(() => {
    if (!open) return undefined;
    if (!canPreview) { setPreview(null); setPreviewLoading(false); return undefined; }
    let cancelled = false;
    setPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const result = await previewRule(buildPreviewPayload());
        if (!cancelled) { setPreview(result); setError(null); }
      } catch (e) {
        if (!cancelled) { setError("Couldn't calculate the impact."); setPreview(null); }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canPreview, ownerId, ruleType, driver, splitTotal, JSON.stringify(splitRows), source?.id, dimension?.id]);

  const handleApply = async () => {
    if (!preview || previewLoading) return;
    setApplyLoading(true);
    setError(null);
    try {
      if (isSplit) {
        await setSplit({
          sourceId: source?.id,
          dimensionId: dimension?.id,
          effectiveFrom: effectiveDate,
          owners: splitRows.map((r) => ({ ownerNodeId: r.ownerNodeId, pct: parseFloat(r.pct) || 0 })),
        });
      } else {
        await createRule({
          sourceId: source?.id,
          dimensionId: dimension?.id,
          ownerNodeId: ownerId,
          ruleType,
          effectiveFrom: effectiveDate,
          ...(ruleType === "usage_based" && { driverKey: driver }),
        });
      }
      onSaved();
    } catch (e) {
      setError(isEditing ? "Couldn't save the rule." : "Couldn't apply the rule.");
    } finally {
      setApplyLoading(false);
    }
  };

  // ── Derived display values ──────────────────────────────────────────────
  // Owners as an indented tree (depth + isParent) in hierarchy order, so the picker mirrors the
  // cost-model structure instead of a flat, seemingly-unordered list. Catch-all is excluded:
  // you never *assign* to it, the remainder covers it.
  const ownerOptions = buildOwnerTreeOptions(nodes, edges, CATCH_ALL_CODE);
  // Options for split row i: drop owners already chosen in OTHER rows (no duplicates), but
  // keep this row's own current selection so it stays visible/selected.
  const splitOwnerOptions = (rowIndex) => {
    const usedElsewhere = new Set(
      splitRows.filter((_, idx) => idx !== rowIndex).map((r) => r.ownerNodeId)
    );
    return ownerOptions.filter(
      (o) => !usedElsewhere.has(o.value) || o.value === splitRows[rowIndex]?.ownerNodeId
    );
  };
  const nameOf = (id) => nodes.find((n) => n.node_id === id)?.name || "the selected owner";
  const sourceName = source?.displayName || "this source";
  const sourceCost = source?.monthlyCost || 0;
  const backendCost = preview?.affected_monthly_cost || 0;
  const baseCost = backendCost > 0 ? backendCost : sourceCost;

  // Per-owner euro amounts (+ remainder as the last entry) computed with largest-remainder
  // rounding so the lines sum exactly to the source total shown in the impact header.
  const splitWeights = [...splitRows.map((r) => parseFloat(r.pct) || 0), splitRemaining];
  const splitEuros = allocateEuros(baseCost, splitWeights);
  const remainderEuro = splitEuros[splitEuros.length - 1];

  const outcomeSentence = (() => {
    switch (ruleType) {
      case "direct":
        return `Assigns all of ${sourceName} to ${nameOf(ownerId)}.`;
      case "usage_based": {
        const d = DRIVERS.find((x) => x.value === driver)?.label || "a usage driver";
        return `Splits ${sourceName} across owners by ${d}.`;
      }
      case "proportional":
        return `Splits ${sourceName} by owners' existing allocation shares.`;
      default:
        return "";
    }
  })();

  const strengthLabel = preview?.confidence_label
    ? preview.confidence_label.charAt(0).toUpperCase() + preview.confidence_label.slice(1)
    : "—";

  const applyDisabled = !preview || previewLoading || applyLoading;

  const footerContent = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm rounded-ctrl border border-border text-text hover:bg-surface-2 transition-colors"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleApply}
        disabled={applyDisabled}
        title={!preview && !previewLoading ? "Complete the rule to enable" : undefined}
        className="px-4 py-2 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        aria-disabled={applyDisabled}
      >
        {applyLoading ? "Saving…" : isEditing ? "Save" : "Apply"}
      </button>
    </div>
  );

  // Owner picker block reused by the single-owner path and each split row. `options`
  // defaults to the full assignable set; split rows pass a deduped subset.
  const renderOwnerPicker = (value, onChange, options = ownerOptions) => (
    <Dropdown
      options={options}
      selectedValue={options.find((o) => o.value === value) || null}
      setSelectedValue={(opt) => onChange(opt?.value ?? null)}
      fullWidth
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
    />
  );

  return (
    <RightSheet
      open={open}
      onClose={onClose}
      title={`${isEditing ? "Edit" : "New"} ${isSplit ? "percentage split" : "allocation rule"}`}
      subtitle={source?.displayName ? `${source.displayName} · ${dimension?.name || "—"}` : undefined}
      footer={footerContent}
    >
      <div className="space-y-5 text-sm text-text">
        {/* Rule type — each option carries a one-line description. */}
        <div>
          <label className="block text-xs font-medium text-text-2 mb-2">Rule type</label>
          <div className="space-y-1.5">
            {RULE_TYPES.map((rt) => (
              <label
                key={rt.value}
                className={`flex items-start gap-2 cursor-pointer rounded-ctrl border px-3 py-2 transition-colors ${
                  ruleType === rt.value ? "border-primary bg-primary-soft" : "border-border hover:bg-surface-2"
                }`}
              >
                <input
                  type="radio"
                  name="rule_type"
                  value={rt.value}
                  checked={ruleType === rt.value}
                  onChange={() => setRuleType(rt.value)}
                  className="accent-primary mt-0.5"
                />
                <span className="leading-tight">
                  <span className="block text-sm text-text">{rt.label}</span>
                  <span className="block text-xs text-text-2">{rt.desc}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Owner / driver / split — depends on rule type. */}
        {nodesLoading ? (
          <p className="text-xs text-text-2 italic">Loading owners…</p>
        ) : nodesError ? (
          <div className="flex items-center gap-2 text-xs text-danger">
            <span>{nodesError}</span>
            <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="underline text-danger hover:text-danger">
              Retry
            </button>
          </div>
        ) : nodes.length === 0 ? (
          <p className="text-xs text-text-2 italic">No owner nodes available for this hierarchy.</p>
        ) : isSplit ? (
          // ── Percentage split editor ──────────────────────────────────────
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className="block text-xs font-medium text-text-2">Owners &amp; percentages</label>
              {sourceCost > 0 && (
                <span className="text-xs text-text-2 tabular-nums">{euro(sourceCost)}/mo to divide</span>
              )}
            </div>
            <div className="space-y-2">
              {splitRows.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="flex-1">{renderOwnerPicker(row.ownerNodeId, (v) => updateSplitRow(i, { ownerNodeId: v }), splitOwnerOptions(i))}</div>
                  <div className="relative w-24">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      value={row.pct}
                      onChange={(e) => updateSplitRow(i, { pct: e.target.value })}
                      placeholder="0"
                      aria-label={`Percentage for owner ${i + 1}`}
                      className="w-full border border-border rounded-ctrl pl-3 pr-6 py-1.5 text-sm tabular-nums"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-text-2">%</span>
                  </div>
                  {/* Fill this row with the remaining %, shown only when there's slack. */}
                  {splitRemaining > 0 ? (
                    <button
                      type="button"
                      onClick={() => assignRemainderTo(i)}
                      title={`Add the remaining ${splitRemaining}% to this owner`}
                      aria-label={`Add remaining ${splitRemaining}% to owner ${i + 1}`}
                      className="text-[11px] text-primary hover:text-primary-hover whitespace-nowrap px-1 tabular-nums"
                    >
                      +{splitRemaining}%
                    </button>
                  ) : (
                    <span className="w-[34px]" aria-hidden />
                  )}
                  <button
                    type="button"
                    onClick={() => removeSplitRow(i)}
                    disabled={splitRows.length === 1}
                    aria-label={`Remove owner ${i + 1}`}
                    className="text-text-2 hover:text-danger disabled:opacity-30 disabled:cursor-not-allowed px-1"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-2 flex items-center justify-between">
              <button
                type="button"
                onClick={addSplitRow}
                className="text-xs text-primary hover:text-primary-hover underline underline-offset-2"
              >
                + Add owner
              </button>
              <button
                type="button"
                onClick={distributeEvenly}
                className="text-xs text-text-2 hover:text-text underline underline-offset-2"
              >
                Distribute evenly
              </button>
            </div>

            {/* Live allocation meter — the answer to "where's the other 75%?". */}
            <div className="mt-3">
              <div className="h-1.5 w-full rounded-full bg-surface-2 overflow-hidden flex">
                <span
                  className={`h-full ${splitOver ? "bg-danger" : "bg-primary"}`}
                  style={{ width: `${Math.min(100, splitTotal)}%` }}
                />
              </div>
              <p className={`mt-1 text-[11px] ${splitOver ? "text-danger" : "text-text-2"}`}>
                {splitOver ? (
                  <>Allocated {round1(splitTotal)}% — over 100%, reduce a percentage.</>
                ) : splitRemaining === 0 ? (
                  <>Fully allocated (100%).</>
                ) : (
                  <>
                    Allocated <span className="tabular-nums">{round1(splitTotal)}%</span> ·{" "}
                    <span className="tabular-nums">{splitRemaining}%</span> → Unallocated
                  </>
                )}
              </p>
            </div>
          </div>
        ) : ruleType === "usage_based" ? (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-text-2 mb-1">Owner</label>
              {renderOwnerPicker(ownerId, setOwnerId)}
            </div>
            <div>
              <label className="block text-xs font-medium text-text-2 mb-1">Driver</label>
              <Dropdown
                options={DRIVERS}
                selectedValue={DRIVERS.find((d) => d.value === driver) || null}
                setSelectedValue={(opt) => setDriver(opt?.value ?? "")}
                fullWidth
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-text-2 mb-1">Owner</label>
            {renderOwnerPicker(ownerId, setOwnerId)}
          </div>
        )}

        {/* Effective from — a rule input (sets when the rule takes effect), always visible. */}
        <div>
          <label className="block text-xs font-medium text-text-2 mb-1">Effective from</label>
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="border border-border rounded-ctrl px-3 py-1.5 text-sm"
          />
          <p className="mt-1 text-[11px] text-text-2">
            When this takes effect — you can back-date it or schedule it ahead.
          </p>
        </div>

        {/* Impact — auto-updates as you edit; no manual preview step, no stale numbers. */}
        <div>
          <h3 className="text-xs font-medium text-text-2 mb-2">Impact</h3>
          {!canPreview ? (
            <p className="text-xs text-text-2 italic">
              {isSplit
                ? "Add at least one owner with a percentage to see the impact."
                : ruleType === "usage_based"
                ? "Choose an owner and driver to see the impact."
                : "Choose an owner to see the impact."}
            </p>
          ) : previewLoading ? (
            <p className="text-xs text-text-2 italic" role="status">Calculating impact…</p>
          ) : preview ? (
            <div className="rounded-card border border-card-bd px-4 py-3 space-y-2">
              {isSplit ? (
                <>
                  <p className="text-sm text-text">
                    Splits {sourceName} across {splitRows.length} owner{splitRows.length === 1 ? "" : "s"}
                    <span className="text-text-2"> · {euro(baseCost)}/mo total</span>
                  </p>
                  <div className="space-y-1">
                    {splitRows.map((row, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-text-2">{nameOf(row.ownerNodeId)} · {round1(parseFloat(row.pct) || 0)}%</span>
                        <span className="tabular-nums text-text">{euro(splitEuros[i])}/mo</span>
                      </div>
                    ))}
                    {/* Remainder line only when there IS a remainder — a €0 line in amber reads
                        as a false warning on a fully-allocated split. */}
                    {splitRemaining > 0 && (
                      <div className="flex justify-between text-xs border-t border-border pt-1">
                        <span className="text-warning">Unallocated · {splitRemaining}%</span>
                        <span className="tabular-nums text-warning">{euro(remainderEuro)}/mo</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-text">{outcomeSentence}</p>
                  {ruleType === "direct" && (
                    <p className="text-lg font-semibold text-text tabular-nums">
                      {euro(baseCost)}<span className="text-xs font-normal text-text-3">/mo</span>
                    </p>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-text-2">Rule strength</span>
                    <span data-testid="rule-strength-badge">
                      <Badge label={strengthLabel} status={strengthStatus(preview.confidence_label)} size="sm" />
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </RightSheet>
  );
};

RuleWizardDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  source: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayName: PropTypes.string,
    monthlyCost: PropTypes.number,
    cells: PropTypes.object,
  }),
  dimension: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default RuleWizardDrawer;
